import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { resolveImage } from "../lib/api";

const CATEGORIES = [
  { key: "all", label: "All Work" },
  { key: "web", label: "Web Design" },
  { key: "social", label: "Social Media" },
  { key: "vr", label: "VR Application" },
  { key: "elearning", label: "E-learning" },
  { key: "game", label: "Game Design" },
];

const ProjectCard = ({ w }) => (
  <Link
    to={`/work/${w.slug || w.id}`}
    data-testid={`work-card-${w.slug || w.id}`}
    className="mk-card group block relative rounded-[22px] overflow-hidden border border-[color:var(--card-border)] aspect-[4/3]"
    style={{ backgroundColor: w.color || "#FFD9C7" }}
  >
    {w.cover && (
      <img
        src={resolveImage(w.cover)}
        alt={w.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
    {w.tag && (
      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-neutral-900 text-[10px] font-medium uppercase tracking-wider">
        {w.tag}
      </div>
    )}
    <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 text-neutral-900 grid place-items-center transition-transform duration-300 group-hover:rotate-45">
      <ArrowUpRight className="w-4 h-4" />
    </span>
    <div className="absolute bottom-3 left-4 right-4 text-white">
      {w.client && (
        <div className="text-[10px] uppercase tracking-wider text-white/80 truncate">
          {w.client}
        </div>
      )}
      <h3 className="mt-0.5 font-display italic text-base md:text-lg font-semibold leading-tight line-clamp-2">
        {w.title}
      </h3>
    </div>
  </Link>
);

const Work = ({ projects = [] }) => {
  const [active, setActive] = useState("all");
  const filtered = useMemo(
    () => (active === "all" ? projects : projects.filter((w) => w.category === active)),
    [active, projects]
  );

  return (
    <section id="work" className="relative py-16 md:py-24 bg-[color:var(--bg-2)]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="mb-8 md:mb-12">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--muted)]">
            Case studies
          </div>
          <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-[1.02] max-w-3xl">
              Explore my <em className="italic text-[color:var(--violet)]">deep works</em>.
            </h2>
            <div className="flex flex-wrap gap-2" data-testid="work-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setActive(c.key)}
                  data-testid={`work-filter-${c.key}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                    active === c.key
                      ? "bg-[color:var(--violet)] text-white border-[color:var(--violet)]"
                      : "bg-transparent text-[color:var(--ink)] border-[color:var(--card-border)] hover:border-[color:var(--ink)]/50"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filtered.length === 0 && (
          <div className="py-12 text-center text-[color:var(--muted)]">
            No projects here yet.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filtered.map((w) => (
            <ProjectCard key={w.id} w={w} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Work;
